import { useEffect, useState } from "react";
import {
	Box,
	Button,
	Container,
	Divider,
	Heading,
	Link,
	List,
	ListIcon,
	ListItem,
	SimpleGrid,
	Stack,
	Text,
} from "@chakra-ui/react";
import { MdCheckCircle } from "react-icons/md";
import { Link as ReactLink } from "react-router-dom";
import { useCmsApi } from "../../services/useCmsApi";
import { IPricingTier } from "../../models/ICmsModels";

const coreFeatures = [
	"Weed history",
	"Resistance detection",
	"Predictive prescriptions",
];

interface IPricingCardProps {
	title: string;
	price?: string;
	period?: string;
	commitment: string;
	description: string;
	features: string[];
	extras?: string[];
	highlighted?: boolean;
}

const PricingCard = ({
	title,
	price,
	period,
	commitment,
	description,
	features,
	extras,
	highlighted,
}: IPricingCardProps): JSX.Element => (
	<Stack
		p="6"
		bg="white"
		color="gray.800"
		borderRadius="xl"
		spacing="5"
		h="full"
		border="1px solid"
		borderColor={highlighted ? "blue.300" : "gray.200"}
		transform={highlighted ? { md: "scale(1.04)" } : undefined}
		boxShadow={highlighted ? "xl" : "sm"}
		position="relative"
		zIndex={highlighted ? 1 : 0}
		overflow="hidden"
	>
		{highlighted ? (
			<Box
				position="absolute"
				top="18px"
				right="-38px"
				transform="rotate(45deg)"
				bg="green.600"
				color="white"
				px="10"
				py="1.5"
				fontSize="xs"
				fontWeight="bold"
				letterSpacing="0.08em"
				textTransform="uppercase"
				boxShadow="sm"
			>
				Featured
			</Box>
		) : null}
		<Stack spacing="1">
			<Heading size={{ base: "lg", md: "lg" }}>{title}</Heading>
			<Text fontSize="xl" fontWeight="semibold" color="gray.600">{commitment}</Text>
		</Stack>
		{price ? (
			<Stack spacing="0">
				<Text fontSize="3xl" fontWeight="extrabold">{price}</Text>
				{period ? <Text fontSize="sm" color="gray.500">{period}</Text> : null}
			</Stack>
		) : null}
		<Text fontSize="sm" color="gray.600">{description}</Text>
		<Divider borderColor="gray.200" />
		<Stack spacing="2" flex="1">
			<Text fontWeight="semibold" fontSize="sm">Includes:</Text>
			<List spacing="1">
				{features.map((f) => (
					<ListItem key={f} fontSize="sm" display="flex" alignItems="flex-start">
						<ListIcon as={MdCheckCircle} color="blue.500" mt="0.15rem" />
						{f}
					</ListItem>
				))}
				{extras?.map((e) => (
					<ListItem key={e} fontSize="sm" display="flex" alignItems="flex-start">
						<ListIcon as={MdCheckCircle} color="blue.500" mt="0.15rem" />
						{e}
					</ListItem>
				))}
			</List>
		</Stack>
		<Link as={ReactLink} to="/contact-us">
			<Button
				w="full"
				bg="blue.600"
				color="white"
				_hover={{ bg: "blue.700" }}
			>
				Contact Us
			</Button>
		</Link>
	</Stack>
);

export const PricingSection = (): JSX.Element => {
	const cmsApi = useCmsApi();
	const [pricingTiers, setPricingTiers] = useState<IPricingTier[]>([]);

	useEffect(() => {
		let cancelled = false;
		cmsApi.getPricingTiers().then((items) => {
			if (!cancelled) {
				setPricingTiers(items);
			}
		}).catch(() => {});
		return () => { cancelled = true; };
	}, []); // eslint-disable-line react-hooks/exhaustive-deps

	const renderPricingCards = () => {
		if (pricingTiers.length > 0) {
			return pricingTiers.map((tier) => {
				const features = tier.features ? tier.features.split("\n").filter(Boolean) : coreFeatures;
				return (
					<PricingCard
						key={tier.id}
						title={tier.title}
						commitment={tier.commitment || ""}
						description=""
						features={features}
						highlighted={tier.isHighlighted}
					/>
				);
			});
		}

		// Fallback to hardcoded pricing
		return (
			<>
				<PricingCard
					title="Single-Season Analysis"
					commitment="Single season"
					description="Try out targeted weed control for a single season"
					features={coreFeatures}
				/>
				<PricingCard
					title="Field Remediation Plan"
					commitment="Three-year commitment"
					description="Three-year agronomic program to focus attention on hot spots and deplete weed seedbank."
					features={coreFeatures}
					extras={[
						"Continually updated analysis to support long-term field recovery",
					]}
					highlighted
				/>
				<Stack
					p="6"
					bg="white"
					borderRadius="xl"
					spacing="5"
					h="full"
					border="1px solid"
					borderColor="gray.200"
				>
					<Heading size={{ base: "lg", md: "lg" }} color="gray.800">Full-Farm Programs</Heading>
					<Text color="gray.600" fontSize="sm" flex="1">
						Ready to scale out weed management across a larger area? Let&rsquo;s discuss.
					</Text>
					<Link as={ReactLink} to="/contact-us">
						<Button
							w="full"
							bg="blue.600"
							color="white"
							_hover={{ bg: "blue.700" }}
						>
							Contact Us
						</Button>
					</Link>
				</Stack>
			</>
		);
	};

	return (
		<Box bg="gray.50" py={{ base: "16", md: "24" }}>
			<Container maxW="1480px">
				<Stack spacing="12">
					<Stack spacing="3" textAlign="center" align="center">
						<Heading size={{ base: "sm", md: "xl" }} color="gray.800">
							Engaging with Geco
						</Heading>
						<Text color="gray.600" fontSize={{ base: "lg", md: "xl" }} maxW="2xl" mx="auto">
							Start with the level of support that fits your farm today, then scale up if you want a broader program.
						</Text>
					</Stack>
					<SimpleGrid
						columns={{ base: 1, md: 3 }}
						spacing="6"
						alignItems="stretch"
					>
						{renderPricingCards()}
					</SimpleGrid>
				</Stack>
			</Container>
		</Box>
	);
};
